import React, {useState} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import emailjs from 'emailjs-com';
import './ContactForm.css'; // We will use the updated CSS

// --- ICONS: Inline SVGs for no-dependency visuals ---
const LocationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C7.589 2 4 5.589 4 10C4 12.899 6.279 16.324 11.272 21.619C11.662 22.039 12.338 22.039 12.728 21.619C17.721 16.324 20 12.899 20 10C20 5.589 16.411 2 12 2ZM12 12C10.895 12 10 11.105 10 10C10 8.895 10.895 8 12 8C13.105 8 14 8.895 14 10C14 11.105 13.105 12 12 12Z" /></svg>;
const PhoneIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19.264 15.064C18.283 14.083 17.519 14.168 17.064 14.623L15.82 15.867C13.606 14.712 11.288 12.394 10.133 10.18L11.377 8.936C11.832 8.481 11.917 7.717 10.936 6.736L7.96 3.76C6.979 2.779 6.216 2.864 5.76 3.32L4.053 5.027C3.12 5.96 3.333 7.208 4.646 9.699C6.273 12.73 8.783 15.936 12.217 18.232C14.777 19.986 16.035 20.106 16.978 19.163L18.684 17.457C19.139 17.001 20.244 16.044 19.264 15.064Z" /></svg>;
const EmailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4C2.897 4 2 4.897 2 6V18C2 19.103 2.897 20 4 20H20C21.103 20 22 19.103 22 18V6C22 4.897 21.103 4 20 4ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" /></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z" /><path d="M13 7H11V12H16V10H13V7Z" /></svg>;

const schema = yup.object().shape({ /* Schema remains the same */
  from_name: yup.string().required('Your name is required.'),
  from_email: yup.string().email('Please enter a valid email.').required('Your email is required.'),
  from_phone: yup.string(),
  message: yup.string().required('A message is required.').min(10, 'Message must be at least 10 characters.'),
});

const ContactForm = () => {
  const [status, setStatus] = useState({ type: '', message: '' });
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => { /* onSubmit function remains the same */
    setStatus({ type: 'info', message: 'Sending...' });
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const adminTemplateID = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID;
    const userTemplateID = process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const sendAdminEmail = emailjs.send(serviceID, adminTemplateID, data, publicKey);
    const sendUserEmail = emailjs.send(serviceID, userTemplateID, data, publicKey);

    try {
      await Promise.all([sendAdminEmail, sendUserEmail]);
      setStatus({ type: 'success', message: 'Message sent successfully! Thank you.' });
      reset();
    } catch (error) {
      console.error('Failed to send email:', error);
      setStatus({ type: 'error', message: 'An error occurred. Please try again later.' });
    }
  };

  return (
    <div className="contact-page-wrapper">
      <div className="contact-container">
        {/* --- LEFT INFO PANEL: NOW CONTAINS THE SPECIFIC CONTENT FROM YOUR IMAGE --- */}
        <div className="info-panel">
          <div className="info-header">
            <h2>Get in <strong>touch</strong></h2>
            <p>We are here when you <em>need</em> us the most.</p>
          </div>
          
          <div className="info-section">
            <h4>The <strong>Office</strong></h4>
            <ul className="info-list">
              <li>
                <div className="icon-wrapper"><LocationIcon /></div>
                <span><strong>Address:</strong> 216, International Wealth Center, VIP Road, Vesu, Surat</span>
              </li>
              <li>
                <div className="icon-wrapper"><PhoneIcon /></div>
                <span><strong>Phone:</strong> 0261-4545322</span>
              </li>
              <li>
                <div className="icon-wrapper"><EmailIcon /></div>
                <span><strong>Email:</strong> team@smartlight.in</span>
              </li>
            </ul>
          </div>

          <div className="info-section">
            <h4>Business <strong>Hours</strong></h4>
            <ul className="info-list">
              <li>
                <div className="icon-wrapper"><ClockIcon /></div>
                <span>Monday - Saturday 11am to 7pm</span>
              </li>
              <li>
                <div className="icon-wrapper"><ClockIcon /></div>
                <span>Sunday - Closed | Can be opened under special circumstances</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* --- RIGHT FORM PANEL (UNCHANGED) --- */}
        <div className="form-panel">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-group"><input id="from_name" {...register('from_name')} placeholder="Full Name" /><p className="error-message">{errors.from_name?.message}</p></div>
            <div className="form-group"><input id="from_email" {...register('from_email')} placeholder="Email Address" /><p className="error-message">{errors.from_email?.message}</p></div>
            <div className="form-group"><input id="from_phone" {...register('from_phone')} placeholder="Phone Number (Optional)" /></div>
            <div className="form-group"><textarea id="message" {...register('message')} rows="5" placeholder="Your Message" /><p className="error-message">{errors.message?.message}</p></div>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              <span>{isSubmitting ? 'Sending...' : 'Submit Now'}</span>
            </button>
            {status.message && (<p className={`status-message status-${status.type}`}>{status.message}</p>)}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;